import { Menu, Transition } from "@headlessui/react";
import { EllipsisHorizontalCircleIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { mainSelectedTab } from "../../atoms";
import {
  businessLicenseFromOffice,
  businessLicenseOfficeId,
  busLicensesAddState,
  busLicensesEditState,
  busLicensesShowState,
  busLPViewData,
  disableBLOfficeField,
  rowForEditBusLicense,
  selectedRowsStateBLP,
} from "../../BusinessLicensesAndPermits/atoms/atoms";
import { useGetBusinessLicenseByOfficeId } from "../../BusinessLicensesAndPermits/hooks/useBusinessLicenseByOfficeId";
import { PERMISSIONS } from "../../../PermissionsConstants";
import ConfirmBoxDelete from "../../../../components/ConfirmBoxDelete";
import { useQueryClient } from "react-query";
import { toast } from "../../../../components/toastNotification";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/solid";
import { OfficeTabs } from "../../../OfficeTab/OfficeTabs";
import { getBLPbyId } from "../../BusinessLicensesAndPermits/api/getBLPbyId";
import { useDeactivateBLP } from "../../BusinessLicensesAndPermits/api/deactivateBLP";
import { IOfficeBusinessLicensesandPermitsProps } from "../types";

const canDeactivateRecord = (record) => {
  const licenseStatusId = Number(record?.licenseStatusId);

  return licenseStatusId === 1 || licenseStatusId === 3;
};

const OfficeBusinessLicensesandPermits = ({
  data,
}: IOfficeBusinessLicensesandPermitsProps) => {
  const history = useHistory();

  const queryClient = useQueryClient();

  const userPermissions = useRecoilValue(permissionsSelector);

  const setSelectedIndex = useSetRecoilState(mainSelectedTab);

  const setShowState = useSetRecoilState(busLicensesShowState);

  const setRowViewData = useSetRecoilState(busLPViewData);

  const setViewFromOffice = useSetRecoilState(businessLicenseFromOffice);

  const setBusinessLicenseOfficeId = useSetRecoilState(businessLicenseOfficeId);

  const setAddState = useSetRecoilState(busLicensesAddState);

  const setEditState = useSetRecoilState(busLicensesEditState);

  const setDisableOfficeField = useSetRecoilState(disableBLOfficeField);

  const setRowToEdit = useSetRecoilState(rowForEditBusLicense);

  const [selectedRowsToDelete, setSelectedRowsToDelete] =
    useRecoilState(selectedRowsStateBLP);

  const [openModalDelete, setOpenModalDelete] = useState(false);

  const [isLoadingEdit, setIsLoadingEdit] = useState(false);

  const deactivateLockRef = useRef(false);

  const { mutateAsync: deactivateBLPByRecordId, isLoading: isDeactivating } =
    useDeactivateBLP();

  const { data: businessLicenseData } = useGetBusinessLicenseByOfficeId(
    data?.officeId,
  );

  const businessLicenseDetails = Array.isArray(businessLicenseData)
    ? businessLicenseData
    : [];

  const editPermission = PERMISSIONS.permission_buslicensesandpermits_edit;

  const deactivatePermission =
    PERMISSIONS.permission_buslicensesandpermits_delete;

  useEffect(() => {
    setSelectedRowsToDelete([]);
  }, [setSelectedRowsToDelete]);

  const handleLink = (moduleName: string) => {
    const pathname = `/officesettings/${moduleName}`;

    const userTabs = OfficeTabs.filter((tab) =>
      userPermissions.includes(tab.permission),
    );

    const index = userTabs.findIndex((tab) => tab.to === pathname);

    setSelectedIndex(index);

    history.push(`${pathname}?view=table`);
  };

  const onClickRecordNumber = (rowData) => {
    setBusinessLicenseOfficeId(rowData?.officeId ?? data?.officeId ?? null);

    setAddState(false);

    setEditState(false);

    setRowViewData(rowData);

    setShowState(true);

    setViewFromOffice(true);

    setDisableOfficeField(true);

    handleLink("buslicensesandpermits");
  };

  const onClickViewAll = () => {
    setBusinessLicenseOfficeId(data?.officeId ?? null);

    setAddState(false);

    setEditState(false);

    setShowState(false);

    setViewFromOffice(false);

    handleLink("buslicensesandpermits");
  };

  const onClickEdit = async (rowDetails: {
    recordId?: number | null;
    officeId?: number | null;
  }) => {
    const recordId = Number(rowDetails?.recordId);

    if (!Number.isInteger(recordId) || recordId <= 0) {
      toast.fire(
        "Edit Failed",
        "The selected Business License and Permit has no valid record ID.",
        "warning",
      );

      return;
    }

    if (isLoadingEdit) {
      return;
    }

    setIsLoadingEdit(true);

    let editRecord;

    try {
      editRecord = await getBLPbyId(recordId);
    } catch (error) {
      console.error("Failed to retrieve BLP record:", error);

      toast.fire(
        "Edit Failed",
        "Cannot retrieve the Business License and Permit record.",
        "error",
      );

      setIsLoadingEdit(false);
      return;
    }

    try {
      const officeId =
        editRecord.officeId ?? rowDetails.officeId ?? data.officeId ?? null;

      setRowToEdit({
        ...editRecord,

        recordId,

        recordName: "LP-" + String(recordId).padStart(4, "0"),

        officeId,

        type: editRecord.type ?? editRecord.busLicensePermitTypeId ?? null,

        createdDate: editRecord.createdDate
          ? String(editRecord.createdDate).slice(0, 10)
          : null,

        issuedDate: editRecord.issuedDate
          ? String(editRecord.issuedDate).slice(0, 10)
          : null,

        renewedDate: editRecord.renewedDate
          ? String(editRecord.renewedDate).slice(0, 10)
          : null,

        expirationDate: editRecord.expirationDate
          ? String(editRecord.expirationDate).slice(0, 10)
          : null,

        licenseStatus: editRecord.licenseStatusId ?? null,

        renewalStatus: editRecord.renewalStatusId ?? null,

        isOldLicense: editRecord.isOldLicense ?? false,

        isLicenseRequired: editRecord.isLicenseRequired ?? false,
      });

      setBusinessLicenseOfficeId(officeId);

      setAddState(false);

      setShowState(false);

      setViewFromOffice(true);

      setDisableOfficeField(true);

      setEditState(true);

      handleLink("buslicensesandpermits");
    } catch (error) {
      console.error("Failed to prepare BLP edit form:", error);

      const errorMessage =
        error instanceof Error ? error.message : String(error);

      toast.fire("Edit Failed", errorMessage, "error");
    } finally {
      setIsLoadingEdit(false);
    }
  };

  const handleSelectedRowsChange = (rows) => {
    const selectedActiveRows = (rows || [])
      .filter(canDeactivateRecord)
      .map((row) => {
        const { tableData, ...record } = row;

        return record;
      });

    if (selectedActiveRows.length === 0) {
      return;
    }

    setSelectedRowsToDelete(selectedActiveRows);

    setOpenModalDelete(true);
  };

  const closeDeactivateModal = () => {
    if (isDeactivating) {
      return;
    }

    setOpenModalDelete(false);

    setSelectedRowsToDelete([]);
  };

  const handleDelete = async () => {
    if (deactivateLockRef.current || isDeactivating) {
      return;
    }

    const recordIds = (selectedRowsToDelete || [])
      .map((row) => Number(row?.recordId))
      .filter((recordId) => Number.isInteger(recordId) && recordId > 0);

    if (recordIds.length === 0) {
      toast.fire(
        "Deactivation Failed",
        "No valid Business License and Permit record was selected.",
        "warning",
      );

      return;
    }

    deactivateLockRef.current = true;

    try {
      await deactivateBLPByRecordId(recordIds);

      setSelectedRowsToDelete([]);

      setOpenModalDelete(false);

      await queryClient.invalidateQueries("GetBusinessLicenseByOfficeId");
    } catch {
      toast.fire(
        "Deactivation Failed",
        "Cannot deactivate the selected Business License and Permit.",
        "error",
      );
    } finally {
      deactivateLockRef.current = false;
    }
  };

  const selectedDeleteCount = selectedRowsToDelete?.length ?? 0;

  const deleteMessage = (
    <div className="text-gray-500 font-normal">
      {`Are you sure you want to deactivate ${selectedDeleteCount} ${
        selectedDeleteCount > 1
          ? "Business Licenses and Permits"
          : "Business License and Permit"
      }?`}
    </div>
  );

  return (
    <>
      {businessLicenseDetails.length > 0 ? (
        businessLicenseDetails.map((record, index) => {
          const canEdit = userPermissions.includes(editPermission);

          const canDeactivate = userPermissions.includes(deactivatePermission);

          const isDeactivateEnabled = canDeactivateRecord(record);

          return (
            <div key={record?.recordId ?? index}>
              <div className="grid grid-cols-1 sm:grid-cols-1">
                <div className="px-4 py-2">
                  <div className="flex justify-between items-center m-1 gap-2">
                    <span className="pr-2">
                      <button
                        type="button"
                        className="blueAndBold underline expandedData fontExpand text-xs cursor-pointer bg-transparent border-0 p-0 text-left"
                        onClick={() => onClickRecordNumber(record)}
                      >
                        {record?.recordId
                          ? `LP-${String(record.recordId).padStart(4, "0")}`
                          : ""}
                      </button>
                    </span>

                    {(canEdit || canDeactivate) && (
                      <Menu as="div" className="relative items-center flex">
                        <div>
                          <Menu.Button className="">
                            <EllipsisHorizontalCircleIcon
                              className="-mr-2 mt-1 h-5 w-5 text-primary bg-transparent rounded-full hover:bg-primary hover:text-white"
                              aria-hidden="true"
                            />
                          </Menu.Button>
                        </div>

                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 top-5 z-20 mt-2 w-28 origin-top-right divide-y divide-gray-100 rounded-md bg-white box-shadow ring-1 ring-black ring-opacity-5 focus:outline-none">
                            {canEdit && (
                              <div className="px-1 py-1">
                                <Menu.Item>
                                  {({ active }) => {
                                    const activeClass = active
                                      ? "bg-gray-100 text-primary"
                                      : "text-quaternary";

                                    return (
                                      <button
                                        type="button"
                                        onClick={() => onClickEdit(record)}
                                        disabled={isLoadingEdit}
                                        className={`flex w-full items-center justify-start gap-1 px-2 py-1 text-left text-xs font-medium underline ${activeClass} disabled:cursor-not-allowed disabled:opacity-50`}
                                      >
                                        <PencilSquareIcon className="h-4 w-4" />

                                        {isLoadingEdit ? "Loading..." : "Edit"}
                                      </button>
                                    );
                                  }}
                                </Menu.Item>
                              </div>
                            )}

                            {canDeactivate && (
                              <div className="px-1 py-1">
                                <Menu.Item>
                                  {({ active }) => {
                                    const activeClass = active
                                      ? "bg-gray-100"
                                      : "";

                                    const enabledClass = isDeactivateEnabled
                                      ? "cursor-pointer text-secondary hover:text-hsecondary underline"
                                      : "cursor-not-allowed text-secondary opacity-50";

                                    return (
                                      <button
                                        type="button"
                                        className={`flex w-full items-center justify-start gap-1 px-2 py-1 text-left text-xs font-medium ${enabledClass} ${activeClass}`}
                                        onClick={() =>
                                          handleSelectedRowsChange([record])
                                        }
                                        disabled={
                                          !isDeactivateEnabled || isDeactivating
                                        }
                                      >
                                        <TrashIcon className="h-4 w-4" />

                                        {isDeactivating
                                          ? "Deactivating..."
                                          : "Deactivate"}
                                      </button>
                                    );
                                  }}
                                </Menu.Item>
                              </div>
                            )}
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    )}
                  </div>

                  <div className="grid lg:grid-cols-4 grid-cols-1">
                    <span className="col-span-2 mx-2 xl:mx-4 expandedLabel fontExpand">
                      Type:
                    </span>

                    <div className="col-span-2 expandedData fontExpand">
                      <span className="pr-2">
                        {record?.busLicensePermitTypeName}
                      </span>
                    </div>
                  </div>

                  <div className="grid lg:grid-cols-4 grid-cols-1">
                    <span className="col-span-2 mx-2 xl:mx-4 expandedLabel fontExpand">
                      Issuer:
                    </span>

                    <button
                      type="button"
                      className="col-span-2 expandedData blueAndBold underline fontExpand cursor-pointer bg-transparent border-0 p-0 text-left"
                      onClick={() => {
                        const vendorId = record?.issuer;

                        history.push({
                          pathname: "/vendortab/vendor",
                          state: {
                            moduleName: "Vendor",
                            subModuleName: "Vendor",
                            recordId: String(vendorId),
                            vendorId,
                            issuer: vendorId,
                            officeId:
                              record?.officeId ?? data?.officeId ?? null,
                            action: "OfficeSettingBusLP",
                          },
                        });

                        globalThis.location.reload();
                      }}
                    >
                      {record?.issuerName}
                    </button>
                  </div>

                  <div className="grid lg:grid-cols-4 grid-cols-1">
                    <div className="col-span-2 sm:grid xl:flex mx-2 xl:mx-4 expandedLabel fontExpand">
                      <span>License/</span>
                      <span>Permit #:</span>
                    </div>

                    <div className="col-span-2 expandedData fontExpand">
                      <span className="pr-2">
                        {record?.licenseOrPermitNumber}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="flex justify-center text-xs italic col-span-3 p-2">
          No Business License and Permit Found
        </div>
      )}

      {businessLicenseDetails.length > 2 && (
        <div className="bg-gray-50 p-2 flex justify-center">
          <button
            type="button"
            onClick={onClickViewAll}
            className="text-primary underline font-semibold"
          >
            View All
          </button>
        </div>
      )}

      <ConfirmBoxDelete
        ModalTitle="Deactivate Business License And Permit"
        qConfirmation={deleteMessage}
        isOpen={openModalDelete}
        onClose={closeDeactivateModal}
        onClickFunction={handleDelete}
        cancelButtonRef={undefined}
      />
    </>
  );
};

export default OfficeBusinessLicensesandPermits;
